import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import SitePicker from './SitePicker';

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureCalibrationsPage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [calibrations, setCalibrations] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [strata, setStrata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const reload = useCallback(() => {
    if (!selectedSite?.id) return;
    setLoading(true); setError('');
    Promise.all([
      fetch(`/v1/flux/sites/${selectedSite.id}/calibrations`, { headers: bearerHeaders() }).then(r => r.json()),
      fetch(`/v1/flux/sites/${selectedSite.id}/strata`, { headers: bearerHeaders() }).then(r => r.json()),
    ])
      .then(([cal, str]) => {
        if (!cal.ok) throw new Error(cal.message);
        setCalibrations(cal.calibrations || []);
        setRecommendation(cal.recommendation || null);
        setStrata(str.strata || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedSite?.id]);

  useEffect(() => { reload(); }, [reload]);

  if (!selectedProject) return <EmptyState message="Pick a project from the header." />;
  if (!selectedSite) return (
    <SitePicker
      project={selectedProject}
      pageTitle="Calibrations"
      pageDescription="Site-level K coefficient measurements. Records re-cal campaigns over time."
      onPickSite={setSelectedSite}
    />
  );

  return (
    <>
      <Helmet><title>Calibrations | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">{selectedProject.name} / {selectedSite.name}</Typography>
            <Typography variant="h4">Calibrations</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Site-level K coefficient measurements. The K coefficient drives flux-gradient accuracy. Long-term goal:
              measure K with our own SequestOmeter network instead of an external EC tower.
            </Typography>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {/* Recommendation */}
          {recommendation && (
            <Alert
              severity={
                recommendation.action === 'recalibrate' ? 'warning' :
                recommendation.action === 'calibrate' ? 'info' : 'success'
              }
              icon={<Iconify icon={
                recommendation.action === 'recalibrate' ? 'eva:alert-circle-fill' :
                recommendation.action === 'calibrate' ? 'eva:flag-fill' : 'eva:checkmark-circle-2-fill'
              } />}
              action={
                <Button color="inherit" size="small" onClick={() => setDialogOpen(true)}>
                  {recommendation.action === 'monitor' ? 'Add anyway' : 'Record calibration'}
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>{capitalize(recommendation.action)}:</strong> {recommendation.reason}
              </Typography>
            </Alert>
          )}

          {/* History */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>History ({calibrations.length})</Typography>
            {loading ? <CircularProgress size={24} /> : (
              <Stack spacing={1.5}>
                {calibrations.map(cal => <CalibrationRow key={cal.id} cal={cal} strata={strata} />)}
                {calibrations.length === 0 && (
                  <Alert severity="info">No calibration campaigns recorded yet.</Alert>
                )}
              </Stack>
            )}
          </Box>
        </Stack>

        <RecordCalibrationDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          siteId={selectedSite.id}
          strata={strata}
          onRecorded={() => { setDialogOpen(false); reload(); }}
        />
      </Container>
    </>
  );
}

function CalibrationRow({ cal, strata }) {
  const stratum = strata.find(s => s.id === cal.stratum_id);
  const isExpired = cal.valid_until && new Date(cal.valid_until) < new Date();
  return (
    <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              K = {cal.k_coefficient?.toFixed(3) ?? '—'}
            </Typography>
            {cal.r_squared != null && (
              <Chip size="small" label={`R² ${cal.r_squared.toFixed(2)}`} variant="outlined" />
            )}
            {stratum
              ? <Chip size="small" label={stratum.name} variant="outlined" />
              : <Chip size="small" label="Site-wide" variant="outlined" />
            }
            {isExpired && <Chip size="small" label="Expired" color="warning" />}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {new Date(cal.started_at).toLocaleDateString()}
            {cal.ended_at ? ` → ${new Date(cal.ended_at).toLocaleDateString()}` : ' → ongoing'}
            {cal.sensors_deployed ? ` · ${cal.sensors_deployed} sensors` : ''}
            {cal.valid_until ? ` · valid until ${new Date(cal.valid_until).toLocaleDateString()}` : ''}
          </Typography>
          {cal.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              {cal.notes}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function RecordCalibrationDialog({ open, onClose, siteId, strata, onRecorded }) {
  const [stratumId, setStratumId] = useState('');
  const [k, setK] = useState('');
  const [r2, setR2] = useState('');
  const [sensorsDeployed, setSensorsDeployed] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const r = await fetch(`/v1/flux/sites/${siteId}/calibrations`, {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({
          started_at: new Date().toISOString(),
          ended_at: new Date().toISOString(),
          stratum_id: stratumId || null,
          k_coefficient: parseFloat(k) || null,
          r_squared: parseFloat(r2) || null,
          sensors_deployed: parseInt(sensorsDeployed, 10) || null,
          valid_until: validUntil || null,
          notes: notes || null,
        }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      onRecorded();
      setStratumId(''); setK(''); setR2(''); setSensorsDeployed(''); setValidUntil(''); setNotes('');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record calibration</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField select label="Scope" size="small" value={stratumId} onChange={e => setStratumId(e.target.value)}>
            <MenuItem value="">Site-wide K</MenuItem>
            {strata.map(s => <MenuItem key={s.id} value={s.id}>Stratum: {s.name}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="K coefficient" size="small" type="number" value={k} onChange={e => setK(e.target.value)} fullWidth />
            <TextField label="R²" size="small" type="number" value={r2} onChange={e => setR2(e.target.value)} fullWidth inputProps={{ min: 0, max: 1, step: 0.01 }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Sensors deployed" size="small" type="number" value={sensorsDeployed} onChange={e => setSensorsDeployed(e.target.value)} fullWidth />
            <TextField label="Valid until" size="small" type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField label="Notes" size="small" value={notes} onChange={e => setNotes(e.target.value)} multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!k || saving}>
          {saving ? 'Saving…' : 'Record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Calibrations</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : ''; }
